import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ShoppingCartIcon, ArrowLeftIcon, AlertCircleIcon, CheckCircleIcon, Loader2Icon, TrashIcon } from 'lucide-react';
import { useCart } from '../components/cart/CartContext';
export const CheckoutPage = () => {
  const navigate = useNavigate();
  const {
    items,
    totalPrice,
    totalItems,
    removeItem,
    clearCart
  } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentMessage, setShowPaymentMessage] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleCompletePurchase = () => {
    setIsProcessing(true);
    // Simulate brief processing delay
    setTimeout(() => {
      setIsProcessing(false);
      setShowPaymentMessage(true);
    }, 1200);
  };
  // Empty cart state
  if (items.length === 0 && !showPaymentMessage) {
    return <div className="min-h-screen bg-gray-50 dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-4346">
        <Navbar scrolled={scrolled} data-id="element-4347" />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-4348">
          <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm" data-id="element-4349">
            <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4350">
              <ShoppingCartIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-4351" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4352">
              Your cart is empty
            </h2>
            <p className="text-gray-500 dark:text-[#7A8F8A] mb-6" data-id="element-4353">
              Add some products to your cart before checking out.
            </p>
            <button onClick={() => navigate('/browse')} className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-lg font-medium hover:shadow-lg transition-all" data-id="element-4354">
              Browse Products
            </button>
          </div>
        </main>
        <Footer data-id="element-4355" />
      </div>;
  }
  // Payment integration message
  if (showPaymentMessage) {
    return <div className="min-h-screen bg-gray-50 dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-4356">
        <Navbar scrolled={scrolled} data-id="element-4357" />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-4358">
          <div className="bg-white dark:bg-[#162825] rounded-xl p-10 text-center shadow-sm" data-id="element-4359">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-4360">
              <AlertCircleIcon className="h-8 w-8 text-amber-600 dark:text-amber-400" data-id="element-4361" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-4362">
              Payment Integration Required
            </h2>
            <p className="text-gray-600 dark:text-[#B0C1BD] mb-2 max-w-md mx-auto" data-id="element-4363">
              Connect Stripe or your preferred payment provider to enable real
              transactions.
            </p>
            <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-8 max-w-md mx-auto" data-id="element-4364">
              This is a demo checkout flow. To accept payments, integrate a
              payment gateway in the checkout handler.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-4365">
              <button onClick={() => {
              setShowPaymentMessage(false);
              navigate('/browse');
            }} className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-lg font-medium hover:shadow-lg transition-all" data-id="element-4366">
                Continue Shopping
              </button>
              <button onClick={() => {
              setShowPaymentMessage(false);
            }} className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#F8FAF9] bg-white dark:bg-transparent rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-4367">
                Back to Cart
              </button>
            </div>
          </div>
        </main>
        <Footer data-id="element-4368" />
      </div>;
  }
  // Main checkout view
  return <div className="min-h-screen bg-gray-50 dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-4369">
      <Navbar scrolled={scrolled} data-id="element-4370" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-4371">
        {/* Back button */}
        <button onClick={() => navigate(-1)} className="flex items-center text-sm text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD] mb-8 transition-colors" data-id="element-4372">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-4373" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8" data-id="element-4374">
          Checkout
        </h1>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-4375">
          {/* Order Items */}
          <div className="flex-1" data-id="element-4376">
            <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm overflow-hidden" data-id="element-4377">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1A3330]" data-id="element-4378">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4379">
                  Order Items ({totalItems})
                </h2>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-[#1A3330]" data-id="element-4380">
                {items.map(item => <div key={item.id} className="flex items-center gap-4 px-6 py-4" data-id="element-4381">
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-[#1A3330] flex-shrink-0" data-id="element-4382">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" onError={e => {
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80';
                  }} data-id="element-4383" />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0" data-id="element-4384">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-4385">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-0.5" data-id="element-4386">
                        Qty: {item.quantity}
                      </p>
                    </div>

                    {/* Price + Remove */}
                    <div className="flex items-center gap-3" data-id="element-4387">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-4388">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                      <button onClick={() => removeItem(item.id)} className="text-gray-400 dark:text-[#7A8F8A] hover:text-red-500 dark:hover:text-red-400 transition-colors" aria-label={`Remove ${item.title}`} data-id="element-4389">
                        <TrashIcon className="h-4 w-4" data-id="element-4390" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:w-80" data-id="element-4391">
            <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm p-6 sticky top-24" data-id="element-4392">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-4393">
                Order Summary
              </h2>

              <div className="space-y-3 mb-4" data-id="element-4394">
                <div className="flex justify-between text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-4395">
                  <span data-id="element-4396">
                    Subtotal ({totalItems} item{totalItems !== 1 ? 's' : ''})
                  </span>
                  <span data-id="element-4397">${totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-4398">
                  <span data-id="element-4399">Processing fee</span>
                  <span data-id="element-4400">$0.00</span>
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-[#1A3330] pt-4 mb-6" data-id="element-4401">
                <div className="flex justify-between text-base font-semibold text-gray-900 dark:text-white" data-id="element-4402">
                  <span data-id="element-4403">Total</span>
                  <span data-id="element-4404">${totalPrice.toFixed(2)}</span>
                </div>
              </div>

              <button onClick={handleCompletePurchase} disabled={isProcessing} className={`w-full flex items-center justify-center px-6 py-3 rounded-lg text-base font-medium text-white bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] hover:shadow-lg transition-all ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-4405">
                {isProcessing ? <span className="flex items-center" data-id="element-4406">
                    <Loader2Icon className="animate-spin h-5 w-5 mr-2" data-id="element-4407" />
                    Processing...
                  </span> : 'Complete Purchase'}
              </button>

              <p className="mt-4 text-xs text-center text-gray-500 dark:text-[#7A8F8A]" data-id="element-4408">
                Secure checkout · All transactions encrypted
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-4409" />
    </div>;
};